import { useCallback, useEffect, useState } from "react";
import { type AppConfig, getAppConfig, setPlanMode, setSandboxMode } from "../../api/client";

export interface UseAppConfigResult {
  appConfig: AppConfig | null;
  modeToggling: boolean;
  modeToggleError: string | null;
  planModeToggling: boolean;
  planModeToggleError: string | null;
  refreshAppConfig: () => Promise<void>;
  handleModeToggle: () => Promise<void>;
  handlePlanModeToggle: () => Promise<void>;
  dismissModeToggleError: () => void;
  dismissPlanModeToggleError: () => void;
}

export function useAppConfig(): UseAppConfigResult {
  const [appConfig, setAppConfig] = useState<AppConfig | null>(null);
  const [modeToggling, setModeToggling] = useState(false);
  const [modeToggleError, setModeToggleError] = useState<string | null>(null);
  const [planModeToggling, setPlanModeToggling] = useState(false);
  const [planModeToggleError, setPlanModeToggleError] = useState<string | null>(null);

  const refreshAppConfig = useCallback(async () => {
    try {
      const cfg = await getAppConfig();
      setAppConfig(cfg);
    } catch (err) {
      console.warn("[useAppConfig] getAppConfig failed:", err);
    }
  }, []);

  useEffect(() => {
    refreshAppConfig();
  }, [refreshAppConfig]);

  // Toggle the backend's sandbox flag. Fails silently with a warning
  // if the backend rejects (409 mid-run); writer sees the pill snap
  // back via refreshAppConfig.
  const handleModeToggle = useCallback(async () => {
    if (!appConfig || modeToggling) return;
    setModeToggling(true);
    setModeToggleError(null);
    try {
      await setSandboxMode(!appConfig.sandbox);
    } catch (err) {
      // The backend's most common rejection is the active-run guard
      // (HTTP 409) — flipping mid-run would leak state across runners.
      // Surface the real message to the writer so they understand
      // WHY the pill didn't move, instead of silently swallowing it.
      const message = err instanceof Error ? err.message : String(err);
      console.warn("[useAppConfig] setSandboxMode failed:", err);
      setModeToggleError(`Couldn't switch mode: ${message}`);
    } finally {
      await refreshAppConfig();
      setModeToggling(false);
    }
  }, [appConfig, modeToggling, refreshAppConfig]);

  // Toggle the backend's plan-mode override. No active-run guard —
  // plan mode is only checked at /api/plan time, so flipping mid-run
  // is harmless. Cycle: null/true → false (force off), false → null
  // (restore YAML default).
  const handlePlanModeToggle = useCallback(async () => {
    if (planModeToggling) return;
    setPlanModeToggling(true);
    setPlanModeToggleError(null);
    const next = appConfig?.plan_mode_override === false ? null : false;
    try {
      await setPlanMode(next);
    } catch (err) {
      const message = err instanceof Error ? err.message : String(err);
      console.warn("[useAppConfig] setPlanMode failed:", err);
      setPlanModeToggleError(`Couldn't toggle plan mode: ${message}`);
    } finally {
      await refreshAppConfig();
      setPlanModeToggling(false);
    }
  }, [appConfig?.plan_mode_override, planModeToggling, refreshAppConfig]);

  return {
    appConfig,
    modeToggling,
    modeToggleError,
    planModeToggling,
    planModeToggleError,
    refreshAppConfig,
    handleModeToggle,
    handlePlanModeToggle,
    dismissModeToggleError: () => setModeToggleError(null),
    dismissPlanModeToggleError: () => setPlanModeToggleError(null),
  };
}
